import React, { useState, useEffect, useCallback } from 'react';
import api, { errMsg } from '../api';
import { formatUGX } from '../mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import RestockDialog from '../components/RestockDialog';
import { Search, Boxes, AlertTriangle, TriangleAlert, Plus, Loader2, SlidersHorizontal, RefreshCw } from 'lucide-react';

const InventoryPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const canManage = ['pharmacy_admin', 'stock_controller'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [restockTarget, setRestockTarget] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get('/products'), api.get('/suppliers')]);
      setRows(p.data);
      setSuppliers(s.data);
    } catch (e) {
      toast({ title: 'Could not load inventory', description: errMsg(e), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const addSupplier = async (name) => {
    setSuppliers((s) => (s.includes(name) ? s : [...s, name]));
    try { await api.post('/suppliers', { name }); } catch (e) { /* non-blocking */ }
  };

  const handleRestock = async (data) => {
    try {
      await api.post('/inventory/goods-received', {
        supplier: data.supplier,
        lines: [{
          product_id: restockTarget.id, batch_no: data.batchNo, expiry_date: data.expDate,
          manufacture_date: data.mfgDate, quantity: data.qty, unit_cost: data.unitCost,
          selling_price: data.sellingPrice,
        }],
      });
      toast({ title: 'Restocked ' + restockTarget.name, description: `+${data.qty} units · batch ${data.batchNo} · ${data.supplier}` });
      setRestockTarget(null);
      load();
    } catch (e) {
      toast({ title: 'Restock failed', description: errMsg(e), variant: 'destructive' });
    }
  };

  const filtered = rows.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()) || (p.generic || '').toLowerCase().includes(query.toLowerCase()));
  const lowStock = rows.filter((p) => p.low_stock).length;
  const expiring = rows.filter((p) => p.near_expiry).length;
  const stockValue = rows.reduce((s, p) => s + p.price * p.available, 0);

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Inventory</h1>
          <p className="text-slate-500 mt-2 text-[15px]">Live stock derived from the movement ledger. Restock, adjust or write off with full traceability.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2 rounded-lg"><RefreshCw size={16} /> Refresh</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mt-6">
        {[
          { l: 'Stock value (retail)', v: formatUGX(stockValue), Icon: Boxes, tone: 'bg-blue-50 text-blue-600' },
          { l: 'Low stock items', v: lowStock, Icon: AlertTriangle, tone: 'bg-amber-50 text-amber-600' },
          { l: 'Near expiry', v: expiring, Icon: TriangleAlert, tone: 'bg-red-50 text-red-600' },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${s.tone}`}><s.Icon size={22} /></div>
            <div><div className="font-display font-extrabold text-2xl text-slate-900">{s.v}</div><div className="text-slate-500 text-sm">{s.l}</div></div>
          </div>
        ))}
      </div>

      <div className="relative max-w-md mt-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search inventory..." className="pl-11 h-11 rounded-xl bg-white border-slate-200" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : (
          <table className="w-full text-[15px] min-w-[880px]">
            <thead>
              <tr className="text-left text-slate-500 text-sm border-b border-slate-100">
                <th className="px-6 py-3 font-semibold">Product</th>
                <th className="px-6 py-3 font-semibold">Category</th>
                <th className="px-6 py-3 font-semibold">Price</th>
                <th className="px-6 py-3 font-semibold">Available</th>
                <th className="px-6 py-3 font-semibold">Earliest expiry</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4"><div className="font-medium text-slate-800">{p.name}</div><div className="text-slate-400 text-xs">{p.generic}</div></td>
                  <td className="px-6 py-4 text-slate-600">{p.category}</td>
                  <td className="px-6 py-4 text-slate-600">{formatUGX(p.price)}</td>
                  <td className="px-6 py-4 font-semibold text-slate-800">{p.available}<span className="text-slate-400 font-normal text-xs"> / {p.on_hand} on hand</span></td>
                  <td className="px-6 py-4 text-slate-600">{p.earliest_expiry || '—'}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1.5 flex-wrap">
                      {p.available <= 0 && <Badge variant="outline" className="border-slate-200 text-slate-500">Out</Badge>}
                      {p.low_stock && p.available > 0 && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Low stock</Badge>}
                      {p.near_expiry && <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Near expiry</Badge>}
                      {!p.low_stock && !p.near_expiry && p.available > 0 && <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">OK</Badge>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {canManage && (
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setAdjustTarget(p)} className="h-9 rounded-lg gap-1.5 text-slate-600"><SlidersHorizontal size={14} /> Adjust</Button>
                        <Button variant="outline" onClick={() => setRestockTarget(p)} className="h-9 rounded-lg gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"><Plus size={15} /> Restock</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RestockDialog product={restockTarget} open={!!restockTarget} onOpenChange={(o) => !o && setRestockTarget(null)}
        suppliers={suppliers} onAddSupplier={addSupplier} onSubmit={handleRestock} />

      <AdjustDialog product={adjustTarget} open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)} onDone={load} />
    </div>
  );
};

// ---------- Adjustment / write-off dialog ----------
const AdjustDialog = ({ product, open, onOpenChange, onDone }) => {
  const { toast } = useToast();
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({ batch_id: '', movement_type: 'ADJUSTMENT', quantity: '', reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({ batch_id: '', movement_type: 'ADJUSTMENT', quantity: '', reason: '' });
      api.get(`/products/${product.id}/batches`).then((r) => setBatches(r.data.filter((b) => b.on_hand !== 0 || true))).catch(() => setBatches([]));
    }
  }, [product]);

  const submit = async () => {
    if (!form.batch_id) { toast({ title: 'Select a batch', variant: 'destructive' }); return; }
    if (!form.quantity || Number(form.quantity) === 0) { toast({ title: 'Enter a quantity', variant: 'destructive' }); return; }
    if (!form.reason.trim()) { toast({ title: 'Reason is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await api.post('/inventory/adjust', {
        product_id: product.id, batch_id: form.batch_id,
        movement_type: form.movement_type, quantity: Number(form.quantity), reason: form.reason.trim(),
      });
      toast({ title: 'Stock adjusted', description: product.name });
      onOpenChange(false); onDone();
    } catch (e) {
      toast({ title: 'Adjustment failed', description: errMsg(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const isWriteOff = form.movement_type !== 'ADJUSTMENT';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Adjust stock: {product?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-slate-700">Type</Label>
            <Select value={form.movement_type} onValueChange={(v) => setForm({ ...form, movement_type: v })}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ADJUSTMENT">Adjustment (count correction, +/-)</SelectItem>
                <SelectItem value="EXPIRY_WRITE_OFF">Expiry write-off</SelectItem>
                <SelectItem value="DAMAGE_WRITE_OFF">Damage write-off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Batch</Label>
            <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Select batch" /></SelectTrigger>
              <SelectContent>
                {batches.map((b) => <SelectItem key={b.batch_id} value={b.batch_id}>{b.batch_no} · exp {b.expiry_date} · on hand {b.on_hand}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">{isWriteOff ? 'Quantity to write off' : 'Quantity (+ to add, − to remove)'}</Label>
            <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder={isWriteOff ? 'e.g. 10' : 'e.g. -3'} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="text-slate-700">Reason (required)</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. physical count correction" className="mt-1.5 h-11" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Saving…' : 'Save adjustment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryPage;
